import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    CircularProgress
} from '@mui/material';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { isValidPhoneNumber } from "libphonenumber-js";
import userManagementStore from '../../store/userManagementStore';
import axiosToken from '../../api/axiosClient';

const ModalAddMember = ({
    open,
    handleClose,
    rolePredefinie = null,
    syncNewMember,
}) => {
    const { getAllRole, isLoadingRole, roles } = userManagementStore();
    const [loading, setLoading] = useState(false);

    // Schéma de validation Yup
    const validationSchema = yup.object({
        nom: yup
            .string()
            .required('Le nom est requis')
            .min(2, 'Le nom doit contenir au moins 2 caractères'),
        prenom: yup
            .string()
            .required('Le prénom est requis')
            .min(2, 'Le prénom doit contenir au moins 2 caractères'),
        email: yup
            .string()
            .email('Email invalide')
            .required("L'email est requis"),
        telephone: yup
            .string()
            .required('Le téléphone est requis')
            .test('is-valid-phone', 'Numéro de téléphone invalide', (value) => {
                if (!value) return false;
                return isValidPhoneNumber(value);
            }),
        roleID: yup
            .string()
            .required('Le rôle est requis')
    });

    // Initialisation de Formik
    const formik = useFormik({
        initialValues: {
            nom: '',
            prenom: '',
            email: '',
            telephone: '',
            roleID: rolePredefinie || ''
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            await handleSubmit(values);
        },
        enableReinitialize: true
    });

    // Récupérer la liste des rôles
    useEffect(() => {
        if (open) {
            getAllRole();
        }
    }, [open]);

    // Réinitialiser le formulaire quand le modal s'ouvre
    useEffect(() => {
        if (open) {
            formik.resetForm({
                values: {
                    nom: '',
                    prenom: '',
                    email: '',
                    telephone: '',
                    roleID: rolePredefinie || ''
                }
            });
        }
    }, [open, rolePredefinie]);

    const handleSubmit = async (values) => {
        setLoading(true);

        try {
            const data = {
                nom: values.nom,
                prenom: values.prenom,
                email: values.email,
                telephone: values.telephone,
                whatsapp: values.telephone,
                roleID: values.roleID.toString()
            };

            // console.log({ data });

            const response = await axiosToken.post("user/members", data);
            // console.log('Membre ajouté:', response.data.user);

            toast.success(` ${getRoleName()} ajouté avec succès`);
            syncNewMember(response.data.user);
            handleClose();

        } catch (error) {
            console.error('Erreur lors de l\'ajout:', error);
            const errorMessage = error.response?.data?.error || 'Erreur lors de l\'ajout du membre';
            toast.error(errorMessage);

            // Gestion des erreurs spécifiques du serveur
            if (error.response?.data?.errors) {
                const serverErrors = error.response.data.errors;
                Object.keys(serverErrors).forEach(key => {
                    formik.setFieldError(key, serverErrors[key][0]);
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const getRoleName = () => {
        if (rolePredefinie) {
            const role = roles.find(r => r.id === rolePredefinie);
            const roleName = role?.name ? role.name.charAt(0).toUpperCase() + role.name.slice(1) : "";
            return roleName || 'membre';
        }
        return 'membre';
    };

    const getModalTitle = () => {
        if (rolePredefinie) {
            return `Ajouter un ${getRoleName()}`;
        }
        return "Ajouter un membre";
    };

    // Fonction pour formater le numéro de téléphone en temps réel
    const handlePhoneChange = (e) => {
        const value = e.target.value;
        formik.setFieldValue('telephone', value);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <Typography variant="h6" component="div" className="font-poppins font-semibold text-[18px] text-title">
                    {getModalTitle()}
                </Typography>
            </DialogTitle>

            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {/* Nom */}
                        <TextField
                            label="Nom *"
                            name="nom"
                            value={formik.values.nom}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.nom && Boolean(formik.errors.nom)}
                            helperText={formik.touched.nom && formik.errors.nom}
                            fullWidth
                            disabled={loading}
                            className="font-nunito text-[14px] text-text"
                        />

                        {/* Prénom */}
                        <TextField
                            label="Prénom *"
                            name="prenom"
                            value={formik.values.prenom}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.prenom && Boolean(formik.errors.prenom)}
                            helperText={formik.touched.prenom && formik.errors.prenom}
                            fullWidth
                            disabled={loading}
                            className="font-nunito text-[14px] text-text"
                        />

                        {/* Email */}
                        <TextField
                            label="Email *"
                            name="email"
                            type="email"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.email && Boolean(formik.errors.email)}
                            helperText={formik.touched.email && formik.errors.email}
                            fullWidth
                            disabled={loading}
                            className="font-nunito text-[14px] text-text"
                        />

                        {/* Téléphone */}
                        <TextField
                            label="Téléphone *"
                            name="telephone"
                            value={formik.values.telephone}
                            onChange={handlePhoneChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.telephone && Boolean(formik.errors.telephone)}
                            helperText={formik.touched.telephone && formik.errors.telephone}
                            fullWidth
                            disabled={loading}
                            placeholder="+229 xx xx xx xx"
                            className="font-nunito text-[14px] text-text"
                        />

                        {/* Sélection du rôle */}
                        {isLoadingRole ? (
                            <Box className="flex items-center gap-2 py-2">
                                <CircularProgress size={16} />
                                <Typography variant="body2" color="text.secondary" className="font-nunito text-[14px] text-text">
                                    Chargement des rôles...
                                </Typography>
                            </Box>
                        ) : (
                            rolePredefinie ? (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }} className="font-nunito text-[14px] text-text">
                                    Rôle : {getRoleName()}
                                </Typography>
                            ) : (
                                <FormControl
                                    fullWidth
                                    error={formik.touched.roleID && Boolean(formik.errors.roleID)}
                                    disabled={loading}
                                >
                                    <InputLabel className="font-nunito text-[14px] text-text">Rôle *</InputLabel>
                                    <Select
                                        name="roleID"
                                        value={formik.values.roleID}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        label="Rôle *"
                                        className="font-nunito text-[14px] text-text"
                                    >
                                        <MenuItem value="">
                                            <em className="font-nunito text-[14px] text-text">Sélectionner un rôle</em>
                                        </MenuItem>
                                        {roles.map((role) => (
                                            <MenuItem key={role.id} value={role.id} className="font-nunito text-[14px] text-text">
                                                {role.name.charAt(0).toUpperCase() + role.name.slice(1)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {formik.touched.roleID && formik.errors.roleID && (
                                        <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }} className="font-nunito text-[14px]">
                                            {formik.errors.roleID}
                                        </Typography>
                                    )}
                                </FormControl>
                            )
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 3, gap: 1 }}>
                    <Button
                        onClick={handleClose}
                        disabled={loading}
                        variant="outlined"
                        className="font-nunito font-semibold text-[14px]"
                    >
                        Annuler
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || !formik.isValid}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                        className="font-nunito font-semibold text-[14px] bg-primary hover:bg-primary/90"
                    >
                        {loading ? `Ajout ${getRoleName()}...` : `Ajouter ${getRoleName()}`}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ModalAddMember;